import { ref } from 'vue';
import { Survey } from '@/modules/Survey';
import Repository from './client/ApiAxiosClient';

const resource = '/survey';

interface SurveyResponse {
  id: number;
  userId: number;
  birthday: string | null;
  gender: number | null;
  area: number | null;
  impression: string | null;
  watchEnvs: {
    id: number;
    surveyId: number;
    watchEnv: number;

    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  }[];
  favoriteGenres: {
    id: number;
    surveyId: number;
    favoriteGenre: number;

    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  }[];

  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export const useSurveyRepository = () => {
  const survey = ref<Survey>();

  /**
   * 特定のユーザのアンケート回答内容を取得する
   * @param userId
   */
  const get = async (userId: number): Promise<Survey> => {
    const { data } = await Repository.get<SurveyResponse>(`${resource}/${userId}`);
    let birthday: Survey['birthday'] = null;
    if (data.birthday) {
      const [y, m] = data.birthday.split('-');
      birthday = `${y}${m}`;
    }
    const envs: Survey['envs'] = data.watchEnvs ? data.watchEnvs.map((v) => v.watchEnv) : [];
    const genres: Survey['genres'] = data.favoriteGenres ? data.favoriteGenres.map((v) => v.favoriteGenre) : [];

    const result = {
      birthday,
      gender: data.gender,
      area: data.area,
      envs,
      genres,
      impression: data.impression,
    };
    survey.value = result;
    return result;
  };

  /**
   * アンケートの回答を登録する
   * @param userId
   * @param survey
   */
  const create = async (userId: number, survey: Survey) => {
    await Repository.post(`${resource}/${userId}`, _convert(survey)).catch((e) => {
      throw e;
    });
  };

  /**
   * アンケートの回答を更新する
   * @param userId
   * @param survey
   */
  const update = async (userId: number, survey: Survey) => {
    await Repository.put(`${resource}/${userId}`, _convert(survey)).catch((e) => {
      throw e;
    });
  };

  const deleteSurvey = async (userId: number | null) => {
    try {
      await Repository.delete(`${resource}/${userId}`);
      return true;
    } catch {
      return false;
    }
  };

  /**
   * APIのリクエスト形式に合わせて変換
   * @param survey
   */
  const _convert = (survey: Survey) => ({
    birthday: survey.birthday,
    gender: survey.gender,
    area: survey.area,
    watch_envs: survey.envs,
    favorite_genres: survey.genres,
    impression: survey.impression,
  });

  return {
    get,
    create,
    update,
    deleteSurvey,
    survey,
  };
};

export type SurveyRepository = ReturnType<typeof useSurveyRepository>;
